import { CalendarIcon } from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon, XCircle } from "lucide-react";
import { type ComponentPropsWithoutRef, useRef } from "react";
import { DayPicker, DayPickerSingleProps } from "react-day-picker";
import { Button, buttonVariants } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { cn } from "../../lib";

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	...props
}: DayPickerSingleProps) {
	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn("p-3", className)}
			classNames={{
				months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
				month: "space-y-4",
				caption: "flex justify-center pt-1 relative items-center",
				caption_label: "text-sm font-medium",
				nav: "space-x-1 flex items-center",
				nav_button: cn(
					buttonVariants({ variant: "outline" }),
					"h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
				),
				nav_button_previous: "absolute left-1",
				nav_button_next: "absolute right-1",
				table: "w-full border-collapse space-y-1",
				head_row: "flex",
				head_cell:
					"text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
				row: "flex w-full mt-2",
				cell: cn(
					"relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
					"[&:has([aria-selected])]:rounded-md",
				),
				day: cn(
					buttonVariants({ variant: "ghost" }),
					"h-8 w-8 p-0 font-normal aria-selected:opacity-100",
				),
				day_range_start: "day-range-start",
				day_range_end: "day-range-end",
				day_selected:
					"bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
				day_today: "bg-accent text-accent-foreground",
				day_outside:
					"day-outside text-muted-foreground opacity-50  aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
				day_disabled: "text-muted-foreground opacity-50",
				day_range_middle:
					"aria-selected:bg-accent aria-selected:text-accent-foreground",
				day_hidden: "invisible",
				...classNames,
			}}
			components={{
				IconLeft: () => <ChevronLeftIcon className="h-4 w-4" />,
				IconRight: () => <ChevronRightIcon className="h-4 w-4" />,
			}}
			{...props}
		/>
	);
}
Calendar.displayName = "Calendar";

type PopoverSide = ComponentPropsWithoutRef<typeof PopoverContent>["side"];

export type CalendarInputProps = Omit<DayPickerSingleProps, "mode"> & {
	triggerClassName?: string;
	formatDate?: string;
	clearButton?: boolean;
	side?: PopoverSide;
	onSelect?: (date: Date | undefined) => void;
};

export function CalendarInput({
	triggerClassName,
	selected,
	formatDate,
	clearButton,
	onSelect,
	side,
	...props
}: CalendarInputProps) {

	const triggerRef = useRef<HTMLButtonElement>(null);
	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onSelect?.(undefined);
	};
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					ref={triggerRef}
					variant={"outline"}
					className={cn(
						"w-full pl-3 text-left font-normal bg-white justify-between shadow",
						!selected && "text-muted-foreground",
						triggerClassName,
					)}
				>
					<span className="flex items-center gap-2">
						<CalendarIcon className="h-4 w-4" />
						<span className="opacity-70">
							{selected ? format(selected, formatDate ?? "PPP") : "Select date"}
						</span>
					</span>
					{(clearButton && 
						<button
							type="button"
							onClick={handleClear}
							className="focus:outline-none hover:opacity-100 transition-opacity"
						>
							<XCircle className="h-4 w-4 opacity-50 hover:opacity-100" />
						</button>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start" side={side} >
				<Calendar mode="single" selected={selected} onSelect={onSelect} initialFocus {...props} />
			</PopoverContent>
		</Popover>
	);
}
